import React, { useState } from 'react';
import { Mail, Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { api, formatApiError } from '../lib/api';

/**
 * Compact email-capture for the footer and blog-tail.
 *
 * Variants:
 *   * "footer" — inverted on dark background, single horizontal row
 *   * "card"  — light card with a heading, for blog post bottoms
 *
 * On submit, posts to /api/public/newsletter (idempotent upsert by
 * email). Shows a tiny inline confirmation; never blocks UI on error.
 */
export default function NewsletterSignup({ variant = 'footer', source = 'footer' }) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState({ busy: false, done: false, err: null });

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email || state.busy) return;
    setState({ busy: true, done: false, err: null });
    try {
      await api.post('/api/public/newsletter', { email: email.trim(), source });
      setState({ busy: false, done: true, err: null });
      setEmail('');
    } catch (err) {
      setState({ busy: false, done: false, err: formatApiError(err) });
    }
  };

  if (variant === 'card') {
    return (
      <div className="rounded-3xl bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 border border-violet-100 p-8 md:p-10">
        <div className="flex items-start gap-4">
          <span className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-500/30">
            <Mail size={22} />
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-extrabold text-slate-900 mb-1">Monthly UK telecoms briefing</h3>
            <p className="text-sm text-slate-600">
              One email per month on what changed in UK voice, contact-centre and Microsoft Teams
              telephony. No marketing fluff. Unsubscribe with one click.
            </p>
            <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-2">
              <label className="flex-1 min-w-0">
                <span className="sr-only">Email</span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.co.uk"
                  className="w-full px-4 py-2.5 rounded-full border border-slate-300 bg-white focus:border-violet-500 focus:ring-4 focus:ring-violet-100 outline-none text-sm"
                  data-testid="newsletter-card-email"
                />
              </label>
              <button
                type="submit"
                disabled={state.busy}
                className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white text-sm font-bold shadow-md hover:shadow-lg transition-shadow disabled:opacity-60"
              >
                {state.busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Subscribe
              </button>
            </form>
            <p className="text-[11px] text-slate-500 mt-2">
              By subscribing you agree to our{' '}
              <a href="/privacy-policy" className="underline hover:text-violet-700">Privacy Policy</a>.
            </p>
            {state.done && (
              <p className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700">
                <CheckCircle2 size={14} /> Welcome aboard. Look out for our next briefing.
              </p>
            )}
            {state.err && (
              <p className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-rose-700">
                <AlertCircle size={14} /> {state.err}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Footer variant — dark, compact.
  return (
    <div>
      <p className="text-white font-bold text-sm mb-2 flex items-center gap-2">
        <Mail size={14} className="text-violet-300" />
        Monthly UK telecoms briefing
      </p>
      <p className="text-xs text-slate-400 mb-3 leading-relaxed">
        One email a month — what changed in UK voice, MS Teams and contact-centre telephony.
      </p>
      <form onSubmit={submit} className="flex gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.co.uk"
          className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-slate-500 text-sm focus:border-violet-400 focus:ring-2 focus:ring-violet-500/20 outline-none"
          data-testid="newsletter-footer-email"
        />
        <button
          type="submit"
          disabled={state.busy}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-sm font-bold shadow-md hover:shadow-lg transition-shadow disabled:opacity-60"
          aria-label="Subscribe"
        >
          {state.busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </button>
      </form>
      {state.done && (
        <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-emerald-300">
          <CheckCircle2 size={12} /> Subscribed — thanks!
        </p>
      )}
      {state.err && (
        <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-rose-300">
          <AlertCircle size={12} /> {state.err}
        </p>
      )}
    </div>
  );
}
